import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Image, Video, FileType, FileText, Layers, FolderPlus, Link2,
  Pencil, Trash2, Music, RotateCcw, ListMusic, Play, Pause,
} from 'lucide-react'
import { Badge } from '@meno/ui/components/ui/badge'
import { BACKEND_URL } from '@meno/ui/lib/api'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { formatBytes, formatDate, formatDuration } from '@meno/ui/lib/utils'
import { ContentCard, type CardMenuItem } from '@/components/ContentCard'
import type { InteractiveLink } from '@/components/CreateLinkDialog'
import PdfThumbnail from '@/components/PdfThumbnail'
import { type Folder, type MediaFile, type AudioFile, type AudioPlaylist, SLOT_LABELS } from './utils'

// Every asset tile below renders through the shared ContentCard so the whole
// library reads as one system: the media/preview sits on top, and the action
// row lives in the bottom footer (a primary-action icon + a 3-dot menu) exactly
// like playlists, layouts and stop screens. Per-type differences are just
// props — a Radio publish icon for "send to screens", an omitted publish in the
// Archive view, drag-and-drop wiring, or a folder's drop-target handlers.

// Tailwind classes layered onto a draggable card while it's actively being
// dragged. We rely on a CSS class instead of an inline style so the visual
// lift can compose with the card's existing hover/focus tokens.
const DRAGGING_CLS = 'opacity-50 scale-[0.97] ring-2 ring-primary/60 ring-offset-1 shadow-lg transition-[transform,opacity,box-shadow]'
// Highlight applied to a drop target (FolderCard) while an asset is hovered
// over it. The dashed primary ring matches the brand drop affordance used
// elsewhere in the dashboard.
const DROP_TARGET_CLS = 'ring-2 ring-primary ring-offset-2 bg-muted shadow-md'

// ── ThumbnailHit ─────────────────────────────────────────────────────────
// The clickable wrapper for a card's thumbnail. When `onPreview` is set we
// render a <button> that calls it; otherwise we fall back to a plain <a> that
// opens the raw asset in a new tab (preserves the old Brand Assets behaviour
// where no preview modal exists). Either way the rendered element is the
// same shape — block / aspect-video / overflow-hidden — so the overlay pills
// keep working untouched.
function ThumbnailHit({
  url, ariaLabel, onPreview, className, children,
}: {
  url:        string
  ariaLabel:  string
  onPreview?: () => void
  className?: string
  children:   React.ReactNode
}) {
  const cls = `block w-full aspect-video bg-muted overflow-hidden relative ${className ?? ''}`
  if (onPreview) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={e => { e.stopPropagation(); e.preventDefault(); onPreview() }}
        className={`${cls} text-start cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30`}
      >
        {children}
      </button>
    )
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={cls}>
      {children}
    </a>
  )
}

// ── MediaThumbnail ──────────────────────────────────────────────────────
// Compact thumbnail cell used in the list-view table. Renders a small
// rounded preview for images, the cached frame (or a video placeholder)
// for videos, and a generic file icon for everything else.

export function MediaThumbnail({ file, thumbCache }: { file: MediaFile; thumbCache: Record<number, string> }) {
  const url = file.url.startsWith('http') ? file.url : `${BACKEND_URL}${file.url}`
  if (file.file_type.startsWith('image')) {
    return (
      <div className="w-16 h-10 rounded-md overflow-hidden bg-muted shrink-0 border border-border/40">
        <img src={url} alt={file.original_name} className="w-full h-full object-cover" loading="lazy" />
      </div>
    )
  }
  if (file.file_type.startsWith('video')) {
    const thumb = thumbCache[file.id]
    if (thumb) {
      return (
        <div className="w-16 h-10 rounded-md overflow-hidden bg-muted shrink-0 border border-border/40 relative">
          <img src={thumb} alt={file.original_name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Video className="w-3 h-3 text-white" />
          </div>
        </div>
      )
    }
    return (
      <div className="w-16 h-10 rounded-md overflow-hidden bg-muted shrink-0 border border-border/40 relative">
        <video src={`${url}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <Video className="w-3 h-3 text-white" />
        </div>
      </div>
    )
  }
  // PDF: render page 1 to a canvas. PdfThumbnail handles its own loading/
  // error fallbacks; a small `targetWidth` keeps the canvas cheap for list rows.
  const isPdfRow = file.file_type === 'application/pdf' || /\.pdf$/i.test(file.original_name)
  if (isPdfRow) {
    return (
      <PdfThumbnail
        url={url}
        targetWidth={128}
        fit="cover"
        className="w-16 h-10 rounded-md shrink-0 border border-border/40"
      />
    )
  }
  return (
    <div className="w-16 h-10 rounded-md bg-muted flex items-center justify-center shrink-0 border border-border/40">
      <FileType className="w-4 h-4 text-muted-foreground" />
    </div>
  )
}

// ── GridCard ────────────────────────────────────────────────────────────
// Asset tile used in the grid view. The type pill (and optional brand-slot
// pill) stay as media overlays; publish / rename / delete now live in the
// shared footer. In the Archive view (`onRestore` set) there's no publish and
// the menu offers Restore / Delete-permanently instead.

export function GridCard({
  file, thumbCache, onDelete, onRename, onPreview, onPublish, onRestore, brandSlot,
  draggable: dragOn, onDragStart, onDragEnd,
}: {
  file:       MediaFile
  thumbCache: Record<number, string>
  onDelete:   () => void
  onRename:   () => void
  onPreview?: () => void
  // Publish this asset directly to screens/groups. Hidden in the Archive view.
  onPublish?: () => void
  // When set, the card is in the Archive view: the menu offers Restore, rename
  // is hidden (you restore before editing), and onDelete becomes permanent.
  onRestore?: () => void
  brandSlot?: string
  draggable?: boolean
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?:   (e: React.DragEvent<HTMLDivElement>) => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const url = file.url.startsWith('http') ? file.url : `${BACKEND_URL}${file.url}`
  const isImage = file.file_type.startsWith('image')
  const isVideo = file.file_type.startsWith('video')
  const isPdf   = file.file_type === 'application/pdf' || /\.pdf$/i.test(file.original_name)
  const thumb   = isVideo ? thumbCache[file.id] : null
  // Local drag state — drives the lift/dim styling so the operator sees the
  // card "pick up" and the rest of the grid clearly turns into drop context.
  const [dragging, setDragging] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Asset mutations are gated on the media permissions (backend enforces too):
  // rename = "modify library" (media:upload), delete = media:delete.
  const canRename = can('media:upload')
  const canDelete = can('media:delete')
  const menu: CardMenuItem[] = onRestore
    ? [
        { icon: RotateCcw, label: t('assets.restore', 'Restore'), onClick: onRestore },
        ...(canDelete ? [{ icon: Trash2, label: t('assets.deletePermanently', 'Delete permanently'), onClick: onDelete, destructive: true }] : []),
      ]
    : [
        ...(canRename ? [{ icon: Pencil, label: t('common.rename', 'Rename'), onClick: onRename }] : []),
        ...(canDelete ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }] : []),
      ]

  return (
    <ContentCard
      title={file.original_name}
      subtitle={file.file_size ? formatBytes(file.file_size) : '—'}
      onOpen={onPreview}
      onPublish={onRestore ? undefined : onPublish}
      publishLabel={t('assets.publishToScreens', 'Publish to screens')}
      menu={menu}
      cardRef={cardRef}
      draggable={dragOn}
      className={dragging ? DRAGGING_CLS : undefined}
      onDragStart={e => {
        // Pin the OS drag image to the card itself so the user sees the
        // actual asset following the cursor instead of a small generic icon.
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect()
          e.dataTransfer.setDragImage(cardRef.current, rect.width / 2, rect.height / 2)
        }
        setDragging(true)
        onDragStart?.(e)
      }}
      onDragEnd={e => {
        setDragging(false)
        onDragEnd?.(e)
      }}
      media={
        <ThumbnailHit url={url} ariaLabel={file.original_name} onPreview={onPreview}>
          {isImage && (
            <img src={url} alt={file.original_name} className="w-full h-full object-cover" loading="lazy" />
          )}
          {isVideo && thumb && (
            <img src={thumb} alt={file.original_name} className="w-full h-full object-cover" />
          )}
          {isVideo && !thumb && (
            // No captured still yet (capture is async / can fail on CORS S3):
            // show the native first frame via the #t=0.1 media fragment so the
            // tile always has a real thumbnail instead of a bare icon.
            <video src={`${url}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          )}
          {isPdf && (
            <PdfThumbnail
              url={url}
              targetWidth={480}
              fit="cover"
              className="w-full h-full"
            />
          )}
          {!isImage && !isVideo && !isPdf && (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <FileType className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          <div className="absolute top-2 start-2">
            {isImage && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-600/80 text-white backdrop-blur-sm">
                <Image className="w-2.5 h-2.5" /> {t('common.image', 'Image')}
              </span>
            )}
            {isVideo && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-600/80 text-white backdrop-blur-sm">
                <Video className="w-2.5 h-2.5" /> {t('common.video', 'Video')}
              </span>
            )}
            {isPdf && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-600/80 text-white backdrop-blur-sm">
                <FileText className="w-2.5 h-2.5" /> {t('common.document', 'Document')}
              </span>
            )}
            {brandSlot && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-600/80 text-white backdrop-blur-sm mt-1">
                <Layers className="w-2.5 h-2.5" /> {SLOT_LABELS[brandSlot] ?? brandSlot}
              </span>
            )}
          </div>
        </ThumbnailHit>
      }
    />
  )
}

// ── FolderCard ──────────────────────────────────────────────────────────
// Client-side folder tile rendered on the "All" tab grid. Shares the same
// shell as the asset cards (delete lives in the footer menu; no publish) and
// still acts as a drop target so assets dragged onto it get filed into it.

export function FolderCard({
  folder, onDelete, onOpen, onDropAsset, count,
}: {
  folder:      Folder
  onDelete:    () => void
  onOpen?:     () => void
  onDropAsset?: (payload: { kind: 'm' | 'a' | 'l'; id: number | string }) => void
  count?:      number
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  // Drag-over highlight. dragenter/leave fire for child nodes too, so we keep
  // a depth counter and only clear the highlight once the cursor has left
  // every descendant — otherwise hovering over the folder icon would flash
  // the highlight off and on as the operator moves across child elements.
  const [hovering, setHovering] = useState(false)
  const depthRef = useRef(0)

  const menu: CardMenuItem[] = can('media:delete')
    ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }]
    : []

  return (
    <ContentCard
      title={folder.name}
      subtitle={formatDate(folder.created_at)}
      onOpen={onOpen}
      menu={menu}
      className={hovering ? DROP_TARGET_CLS : undefined}
      rootProps={{
        onDragEnter: e => {
          if (!onDropAsset) return
          if (!e.dataTransfer.types.includes('application/x-meno-asset')) return
          depthRef.current += 1
          setHovering(true)
        },
        onDragOver: e => {
          if (!onDropAsset) return
          if (e.dataTransfer.types.includes('application/x-meno-asset')) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
          }
        },
        onDragLeave: () => {
          if (!onDropAsset) return
          depthRef.current = Math.max(0, depthRef.current - 1)
          if (depthRef.current === 0) setHovering(false)
        },
        onDrop: e => {
          depthRef.current = 0
          setHovering(false)
          if (!onDropAsset) return
          const raw = e.dataTransfer.getData('application/x-meno-asset')
          if (!raw) return
          e.preventDefault()
          try {
            const payload = JSON.parse(raw) as { kind: 'm' | 'a' | 'l'; id: number | string }
            if (payload && payload.kind && payload.id !== undefined) onDropAsset(payload)
          } catch { /* malformed — ignore */ }
        },
      }}
      media={
        <div className="block w-full aspect-video bg-muted overflow-hidden relative">
          <div className="w-full h-full flex items-center justify-center">
            {/* Folder glyph built inline so the colour tracks the brand token. */}
            <svg viewBox="0 0 64 48" className="w-12 h-9 text-primary fill-current">
              <path d="M4 12c0-2.2 1.8-4 4-4h16l4 5h28c2.2 0 4 1.8 4 4v23c0 2.2-1.8 4-4 4H8c-2.2 0-4-1.8-4-4V12z" />
            </svg>
          </div>
          <div className="absolute top-2 start-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary text-primary-foreground backdrop-blur-sm">
              <FolderPlus className="w-2.5 h-2.5" /> {t('common.folder', 'Folder')}
            </span>
          </div>
          {typeof count === 'number' && count > 0 && (
            <div className="absolute bottom-2 end-2">
              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-background/85 text-muted-foreground backdrop-blur-sm border border-border/40">
                {t('assets.folderItemCount', { defaultValue: '{{count}} items', count })}
              </span>
            </div>
          )}
        </div>
      }
    />
  )
}

// ── AudioCard ──────────────────────────────────────────────────────────
// Tile rendered for an audio asset on the unified grid. The inline <audio>
// scrubber rides in the footer's `footerExtra` slot so the operator can audition
// without opening the preview; publish / rename / delete match GridCard.

export function AudioCard({ file, onDelete, onRename, onPreview, onPublish, onRestore, draggable: dragOn, onDragStart, onDragEnd }: {
  file:        AudioFile
  onDelete:    () => void
  onRename:    () => void
  onPreview?:  () => void
  // Stream this track to screens/groups (Audio Hub). Hidden in the Archive view.
  onPublish?:  () => void
  // See GridCard.onRestore — present only in the Archive view.
  onRestore?:  () => void
  draggable?:  boolean
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragEnd?:   (e: React.DragEvent<HTMLDivElement>) => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canRename = can('media:upload')
  const canDelete = can('media:delete')
  const url = file.url.startsWith('http') ? file.url : `${BACKEND_URL}${file.url}`
  const [dragging, setDragging] = useState(false)
  // Track length. Seed from the persisted value, then backfill from the inline
  // <audio> element's metadata so older tracks (uploaded before we captured
  // duration) still show their length once their headers load.
  const [duration, setDuration] = useState<number | undefined>(file.duration)
  const cardRef = useRef<HTMLDivElement>(null)
  // Audition state for the thumbnail play overlay. The (hidden) <audio> element
  // owns the truth; onPlay/onPause/onEnded keep `playing` in sync so the toggle
  // icon reflects reality even when playback ends on its own.
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const togglePlay = () => {
    const el = audioRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => {})
    else el.pause()
  }

  const menu: CardMenuItem[] = onRestore
    ? [
        { icon: RotateCcw, label: t('assets.restore', 'Restore'), onClick: onRestore },
        ...(canDelete ? [{ icon: Trash2, label: t('assets.deletePermanently', 'Delete permanently'), onClick: onDelete, destructive: true }] : []),
      ]
    : [
        ...(canRename ? [{ icon: Pencil, label: t('common.rename', 'Rename'), onClick: onRename }] : []),
        ...(canDelete ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }] : []),
      ]

  const metaLine = [
    file.file_size ? formatBytes(file.file_size) : '—',
    duration ? formatDuration(duration) : null,
  ].filter(Boolean).join(' · ')

  return (
    <ContentCard
      title={file.original_name}
      subtitle={metaLine}
      onOpen={onPreview}
      onPublish={onRestore ? undefined : onPublish}
      publishLabel={t('audioHub.stream', 'Stream to screens')}
      menu={menu}
      cardRef={cardRef}
      draggable={dragOn}
      className={dragging ? DRAGGING_CLS : undefined}
      onDragStart={e => {
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect()
          e.dataTransfer.setDragImage(cardRef.current, rect.width / 2, rect.height / 2)
        }
        setDragging(true)
        onDragStart?.(e)
      }}
      onDragEnd={e => {
        setDragging(false)
        onDragEnd?.(e)
      }}
      media={
        // Audition lives on the thumbnail: a centered play/pause toggle over
        // the tinted art, driving a hidden <audio>. Clicking the button stops
        // propagation so it never triggers the card's open-preview onClick;
        // clicking the surrounding art still opens the preview.
        <div className="relative w-full aspect-video bg-pink-500/10 flex items-center justify-center overflow-hidden">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); e.preventDefault(); togglePlay() }}
            aria-label={playing ? t('common.pause', 'Pause') : t('common.play', 'Play')}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-pink-600 text-white shadow-md transition hover:bg-pink-700 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
          >
            {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ms-0.5" />}
          </button>
          <div className="absolute top-2 start-2 flex items-center gap-1">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-pink-600/80 text-white backdrop-blur-sm">
              <Music className="w-2.5 h-2.5" /> {t('common.audio', 'Audio')}
            </span>
            {duration ? (
              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded bg-black/50 text-white backdrop-blur-sm tabular-nums">
                {formatDuration(duration)}
              </span>
            ) : null}
          </div>
          <audio
            ref={audioRef}
            src={url}
            preload="metadata"
            className="hidden"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onLoadedMetadata={e => {
              const d = e.currentTarget.duration
              if (Number.isFinite(d) && d > 0) setDuration(Math.round(d))
            }}
          />
        </div>
      }
    />
  )
}

// ── AudioPlaylistCard ─────────────────────────────────────────────────────
// Tile for an audio-only playlist on the Audio Hub grid. Same shell as the
// asset cards: a Stream (Radio) publish icon and a Delete menu entry in the
// footer, and the media opens the playlist editor.

export function AudioPlaylistCard({ playlist, onOpen, onStream, onDelete }: {
  playlist: AudioPlaylist
  onOpen:   () => void
  // Optional: gated on playlists:edit by the caller — hidden without it.
  onStream?: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const menu: CardMenuItem[] = can('media:delete')
    ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }]
    : []
  return (
    <ContentCard
      title={playlist.name}
      subtitle={t('audioHub.trackCount', { defaultValue: '{{count}} track', count: playlist.track_count })}
      onOpen={onOpen}
      onPublish={onStream}
      publishLabel={t('audioHub.stream', 'Stream to screens')}
      menu={menu}
      media={
        <div className="flex w-full aspect-video bg-pink-500/10 overflow-hidden relative items-center justify-center">
          <ListMusic className="w-10 h-10 text-pink-500" />
          <div className="absolute top-2 start-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-pink-600/80 text-white backdrop-blur-sm">
              <ListMusic className="w-2.5 h-2.5" /> {t('audioHub.playlist', 'Playlist')}
            </span>
          </div>
        </div>
      }
    />
  )
}

// ── LinkCard ────────────────────────────────────────────────────────────
// Mirrors the asset tiles so webpages read as just another asset type in the
// All-tab grid. The "Webpage" pill matches the coloured pill family; publish /
// edit / delete live in the footer.

export function LinkCard({ link, onEdit, onDelete, onPreview, onPublish }: {
  link:     InteractiveLink
  onEdit:   () => void
  onDelete: () => void
  onPreview?: () => void
  onPublish?: () => void
}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  let host = link.interactive_link
  try { host = new URL(link.interactive_link).hostname.replace(/^www\./, '') } catch {}
  // Click target priority: onPreview → onEdit. When a preview modal is wired
  // up the card surface opens that; if it isn't, the click still opens the
  // edit dialog so the card never reads as inert.
  const menu: CardMenuItem[] = [
    ...(can('media:upload') ? [{ icon: Pencil, label: t('common.edit', 'Edit'), onClick: onEdit }] : []),
    ...(can('media:delete') ? [{ icon: Trash2, label: t('common.delete', 'Delete'), onClick: onDelete, destructive: true }] : []),
  ]
  return (
    <ContentCard
      title={link.name}
      subtitle={host}
      onOpen={onPreview ?? onEdit}
      onPublish={onPublish}
      publishLabel={t('assets.publishToScreens', 'Publish to screens')}
      menu={menu}
      media={
        <div className="flex w-full aspect-video bg-emerald-500/10 overflow-hidden relative items-center justify-center">
          <Link2 className="w-10 h-10 text-emerald-500" />
          <div className="absolute top-2 start-2">
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-600/80 text-white backdrop-blur-sm">
              <Link2 className="w-2.5 h-2.5" /> {t('common.link', 'Webpage')}
            </span>
          </div>
        </div>
      }
    />
  )
}

// ── TypeBadge ───────────────────────────────────────────────────────────
// Pill used in the list view "Type" column. Mirrors the colored pill
// family used by GridCard's overlay badges.

export function TypeBadge({ type }: { type: string }) {
  const { t } = useTranslation()
  if (type.startsWith('image')) return <Badge variant="secondary" className="text-[10px] gap-1 py-0 h-5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0"><Image className="w-2.5 h-2.5" />{t('common.image', 'Image')}</Badge>
  if (type.startsWith('video')) return <Badge variant="secondary" className="text-[10px] gap-1 py-0 h-5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0"><Video className="w-2.5 h-2.5" />{t('common.video', 'Video')}</Badge>
  if (type === 'application/pdf') return <Badge variant="secondary" className="text-[10px] gap-1 py-0 h-5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-0"><FileText className="w-2.5 h-2.5" />{t('common.document', 'Document')}</Badge>
  return <Badge variant="secondary" className="text-[10px] py-0 h-5">{t('common.file', 'File')}</Badge>
}
